import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { updateDoc, doc } from "firebase/firestore";
import { fetchExchangeRate } from "../../utils/api";

const ExpenseTab = ({ trip, theme, onUpdateTrip, isDarkMode }) => {
  const [transactions, setTransactions] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(0.23);

  // 🌟 State สำหรับฝั่งขวา (Dashboard รายวัน)
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("all");

  useEffect(() => {
    const txs = trip.expenses || [];
    setTransactions(txs);
    fetchExchangeRate().then(setExchangeRate);

    // ดึงวันที่ทั้งหมดที่มี transaction เพื่อตั้งค่า default ให้ฝั่งขวา
    if (txs.length > 0) {
      const uniqueDates = [...new Set(txs.map((t) => t.date))].sort(
        (a, b) => new Date(a) - new Date(b)
      );
      if (!selectedDate || !uniqueDates.includes(selectedDate)) {
        setSelectedDate(uniqueDates[0]);
      }
    } else {
      setSelectedDate(null);
    }
  }, [trip.expenses]);

  // 🌟 1. ตั้งค่าหมวดหมู่เริ่มต้น
  const defaultCategories = [
    { id: "food", label: "อาหาร", icon: "🍔", color: "#e67e22" },
    { id: "travel", label: "เดินทาง", icon: "🚕", color: "#3498db" },
    { id: "shopping", label: "ช้อปปิ้ง", icon: "🛍️", color: "#e74c3c" },
    { id: "stay", label: "ที่พัก", icon: "🏨", color: "#9b59b6" },
    { id: "activity", label: "กิจกรรม", icon: "🎟️", color: "#1abc9c" },
    { id: "other", label: "อื่นๆ", icon: "📝", color: "#7f8c8d" },
  ];

  const [customCategories, setCustomCategories] = useState(
    trip.customExpenseCategories || []
  );
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  const allCategories = [...defaultCategories, ...customCategories];

  const initialFormState = {
    type: "expense",
    title: "",
    inputCost: "",
    currency: "THB",
    splitCount: 1,
    date: trip.start || new Date().toISOString().split("T")[0],
    category: "food",
    note: "",
  };

  const [formData, setFormData] = useState(initialFormState);

  // คำนวณยอดแบบ Real-time
  const currentInputCost = parseFloat(formData.inputCost) || 0;
  const amountTHB =
    formData.currency === "JPY"
      ? currentInputCost * exchangeRate
      : currentInputCost;
  const splitCount = parseInt(formData.splitCount) || 1;
  const amountPerPerson = splitCount > 1 ? amountTHB / splitCount : amountTHB;

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      setIsAddingCategory(false);
      return;
    }
    const newCat = {
      id: `exp_cat_${Date.now()}`,
      label: newCategoryName.trim(),
      icon: "🏷️",
      color: "#2ed573",
    };
    const updatedCategories = [...customCategories, newCat];
    try {
      await updateDoc(doc(db, "trips", trip.id), {
        customExpenseCategories: updatedCategories,
      });
      trip.customExpenseCategories = updatedCategories;
      setCustomCategories(updatedCategories);
      setFormData({ ...formData, category: newCat.id });
      setNewCategoryName("");
      setIsAddingCategory(false);
    } catch (error) {
      console.error("Error saving custom category:", error);
      alert("เกิดข้อผิดพลาดในการบันทึกหมวดหมู่");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.inputCost)
      return alert("กรุณากรอกชื่อรายการและจำนวนเงิน");

    const newTx = {
      ...formData,
      id: editingId || `tx_${Date.now()}`,
      inputCost: parseFloat(formData.inputCost),
      amount: amountTHB,
      splitCount: parseInt(formData.splitCount) || 1,
      amountPerPerson: amountPerPerson,
    };

    let newList;
    if (editingId) {
      newList = transactions.map((t) => (t.id === editingId ? newTx : t));
    } else {
      newList = [newTx, ...transactions];
    }

    // เรียงตามวันที่ (ล่าสุดอยู่บนสุด)
    newList.sort((a, b) => new Date(b.date) - new Date(a.date));

    setTransactions(newList);
    onUpdateTrip(newList, "expenses");
    setIsAdding(false);
    setEditingId(null);
    setFormData(initialFormState);

    // Auto select date after adding/editing
    if (!selectedDate || newTx.date !== selectedDate) {
      setSelectedDate(newTx.date);
      setSelectedCategoryFilter("all");
    }

    try {
      await updateDoc(doc(db, "trips", trip.id), { expenses: newList });
    } catch (error) {
      console.error("Error saving expense:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("คุณต้องการลบรายการนี้ใช่หรือไม่?")) {
      const newList = transactions.filter((t) => t.id !== id);
      setTransactions(newList);
      onUpdateTrip(newList, "expenses");
      setEditingId(null);
      try {
        await updateDoc(doc(db, "trips", trip.id), { expenses: newList });
      } catch (error) {
        console.error("Error deleting expense:", error);
      }
    }
  };

  const handleEditClick = (item) => {
    setFormData({
      ...initialFormState,
      ...item,
      inputCost: item.inputCost !== undefined ? item.inputCost : item.amount,
      currency: item.currency || "THB",
    });
    setEditingId(item.id);
    setIsAdding(false);

    // สลับฝั่งขวาให้แสดงวันที่ของรายการที่กำลังแก้ด้วย
    if (item.date !== selectedDate) {
      setSelectedDate(item.date);
      setSelectedCategoryFilter("all");
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData(initialFormState);
  };

  // --- คำนวณสรุปยอดทั้งหมด (สำหรับฝั่งซ้าย) ---
  const totalIncome = transactions
    .filter((t) => t.type === "income")
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  const myTotalExpense = transactions
    .filter((t) => t.type === "expense")
    .reduce((sum, t) => sum + (t.amountPerPerson || t.amount || 0), 0);

  const balance = totalIncome - myTotalExpense;

  // --- ข้อมูลสำหรับฝั่งขวา (Dashboard รายวัน) ---
  const uniqueDates = [...new Set(transactions.map((t) => t.date))].sort(
    (a, b) => new Date(a) - new Date(b)
  );

  const dayTransactions = transactions.filter((t) => t.date === selectedDate);
  const dayExpenses = dayTransactions.filter((t) => t.type === "expense");
  const dayIncomeTotal = dayTransactions
    .filter((t) => t.type === "income")
    .reduce((sum, t) => sum + (t.amount || 0), 0);
  const dayExpenseTotal = dayExpenses.reduce(
    (sum, t) => sum + (t.amountPerPerson || t.amount || 0),
    0
  );

  const dayCategoryTotals = {};
  dayExpenses.forEach((t) => {
    const cat = t.category || "other";
    const cost = t.amountPerPerson || t.amount || 0;
    dayCategoryTotals[cat] = (dayCategoryTotals[cat] || 0) + cost;
  });

  const dayBreakdownArray = Object.keys(dayCategoryTotals)
    .map((key) => {
      const catMeta = allCategories.find((c) => c.id === key) || {
        label: "อื่นๆ",
        color: "#7f8c8d",
        icon: "📝",
      };
      return {
        id: key,
        total: dayCategoryTotals[key],
        label: catMeta.label,
        color: catMeta.color,
        icon: catMeta.icon,
      };
    })
    .sort((a, b) => b.total - a.total);

  // 🌟 Logic สำหรับแสดงข้อความกลาง Donut Chart ตามตัวกรองที่เลือก
  const activeCategoryData =
    selectedCategoryFilter !== "all"
      ? dayBreakdownArray.find((c) => c.id === selectedCategoryFilter)
      : null;

  const displayedDonutTotal = activeCategoryData
    ? activeCategoryData.total
    : dayExpenseTotal;
  const displayedDonutLabel = activeCategoryData
    ? activeCategoryData.label
    : "รายจ่ายรวม";

  // สไตล์พื้นฐาน
  const inputStyle = {
    padding: "10px 12px",
    borderRadius: "8px",
    border: `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
    backgroundColor: isDarkMode ? "#2c2c2c" : "#fff",
    color: isDarkMode ? "#fff" : "#000",
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "bold",
    color: isDarkMode ? "#aaa" : "#666",
    marginBottom: "5px",
    display: "block",
  };

  const cardStyle = {
    backgroundColor: theme?.cardBg || (isDarkMode ? "#1e1e1e" : "#fff"),
    border: `1px solid ${theme?.border || (isDarkMode ? "#333" : "#eee")}`,
    borderRadius: "12px",
    padding: "15px",
    marginBottom: "10px",
  };

  // 🌟 Component สำหรับฟอร์ม
  const renderForm = () => (
    <div
      style={{
        backgroundColor: isDarkMode ? "#0f172a" : "#fff",
        border: "2px solid #3b82f6",
        borderRadius: "12px",
        padding: "20px",
        marginBottom: "15px",
        boxShadow: "0 8px 25px rgba(59, 130, 246, 0.15)",
      }}
    >
      <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
        <button
          type="button"
          onClick={() => setFormData({ ...formData, type: "expense" })}
          style={{
            flex: 1,
            padding: "10px",
            borderRadius: "8px",
            border: "none",
            fontWeight: "bold",
            cursor: "pointer",
            background:
              formData.type === "expense"
                ? "#ef4444"
                : isDarkMode
                ? "#1e293b"
                : "#e2e8f0",
            color:
              formData.type === "expense"
                ? "#fff"
                : isDarkMode
                ? "#94a3b8"
                : "#64748b",
            transition: "0.2s",
          }}
        >
          💸 รายจ่าย
        </button>
        <button
          type="button"
          onClick={() => setFormData({ ...formData, type: "income" })}
          style={{
            flex: 1,
            padding: "10px",
            borderRadius: "8px",
            border: "none",
            fontWeight: "bold",
            cursor: "pointer",
            background:
              formData.type === "income"
                ? "#10b981"
                : isDarkMode
                ? "#1e293b"
                : "#e2e8f0",
            color:
              formData.type === "income"
                ? "#fff"
                : isDarkMode
                ? "#94a3b8"
                : "#64748b",
            transition: "0.2s",
          }}
        >
          💰 รายรับ
        </button>
      </div>

      <form
        onSubmit={handleSave}
        style={{ display: "flex", flexDirection: "column", gap: "15px" }}
      >
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 250px" }}>
            <label style={labelStyle}>ชื่อรายการ</label>
            <input
              type="text"
              placeholder="เช่น ค่าอาหารเย็น, ค่าตั๋วรถไฟ"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              style={{ ...inputStyle, fontWeight: "bold" }}
              required
            />
          </div>
          <div style={{ flex: "1 1 120px" }}>
            <label style={labelStyle}>วันที่</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) =>
                setFormData({ ...formData, date: e.target.value })
              }
              style={inputStyle}
              required
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <div style={{ flex: "1 1 250px" }}>
            <label style={labelStyle}>จำนวนเงิน</label>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                type="number"
                step="any"
                placeholder="0"
                value={formData.inputCost}
                onChange={(e) =>
                  setFormData({ ...formData, inputCost: e.target.value })
                }
                style={{
                  ...inputStyle,
                  color: formData.type === "expense" ? "#ef4444" : "#10b981",
                  fontWeight: "bold",
                  fontSize: "16px",
                  flex: 2,
                }}
                required
              />
              <select
                value={formData.currency}
                onChange={(e) =>
                  setFormData({ ...formData, currency: e.target.value })
                }
                style={{ ...inputStyle, flex: 1, padding: "10px" }}
              >
                <option value="THB">🇹🇭 THB</option>
                <option value="JPY">🇯🇵 JPY</option>
              </select>
            </div>
            {formData.currency === "JPY" && currentInputCost > 0 && (
              <div
                style={{ fontSize: "12px", color: "#888", marginTop: "6px" }}
              >
                แปลงเป็นเงินไทย ≈ ฿
                {amountTHB.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </div>
            )}
          </div>

          {formData.type === "expense" && (
            <div style={{ flex: "1 1 120px" }}>
              <label style={labelStyle}>หารกี่คน? (คน)</label>
              <input
                type="number"
                min="1"
                value={formData.splitCount}
                onChange={(e) =>
                  setFormData({ ...formData, splitCount: e.target.value })
                }
                style={{ ...inputStyle, textAlign: "center" }}
              />
              {splitCount > 1 && amountTHB > 0 && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#e67e22",
                    marginTop: "6px",
                    fontWeight: "bold",
                  }}
                >
                  👉 ตกคนละ ฿
                  {amountPerPerson.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <label style={labelStyle}>หมวดหมู่</label>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {allCategories.map((c) => {
              const isSelected = formData.category === c.id;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, category: c.id })}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "20px",
                    border: isSelected
                      ? `1px solid ${c.color || "#3b82f6"}`
                      : `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
                    background: isSelected
                      ? `${c.color || "#3b82f6"}20`
                      : "transparent",
                    color: isSelected
                      ? c.color || "#3b82f6"
                      : isDarkMode
                      ? "#94a3b8"
                      : "#64748b",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "bold",
                    transition: "0.2s",
                  }}
                >
                  {c.icon} {c.label}
                </button>
              );
            })}

            {isAddingCategory ? (
              <div style={{ display: "flex", gap: "4px" }}>
                <input
                  type="text"
                  placeholder="ชื่อหมวดหมู่..."
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCategory();
                    }
                  }}
                  autoFocus
                  style={{
                    ...inputStyle,
                    padding: "6px 10px",
                    fontSize: "12px",
                    width: "120px",
                    borderRadius: "20px",
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddCategory}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "20px",
                    border: "none",
                    background: "#10b981",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  ✓
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingCategory(false);
                    setNewCategoryName("");
                  }}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "20px",
                    border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
                    background: "transparent",
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsAddingCategory(true)}
                style={{
                  padding: "8px 12px",
                  borderRadius: "20px",
                  border: `1px dashed ${isDarkMode ? "#475569" : "#cbd5e1"}`,
                  backgroundColor: "transparent",
                  color: isDarkMode ? "#94a3b8" : "#64748b",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                + เพิ่ม
              </button>
            )}
          </div>
        </div>

        <div>
          <label style={labelStyle}>บันทึกเพิ่มเติม (ใครจ่าย, โอนให้ใคร)</label>
          <input
            type="text"
            placeholder="เช่น A จ่ายไปก่อนเดี๋ยวโอนคืน"
            value={formData.note}
            onChange={(e) => setFormData({ ...formData, note: e.target.value })}
            style={inputStyle}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "flex-end",
            marginTop: "10px",
          }}
        >
          {editingId && (
            <button
              type="button"
              onClick={() => handleDelete(editingId)}
              style={{
                padding: "10px 16px",
                borderRadius: "8px",
                border: "none",
                background: "rgba(239, 68, 68, 0.1)",
                color: "#ef4444",
                cursor: "pointer",
                fontWeight: "bold",
                marginRight: "auto",
              }}
            >
              🗑️ ลบ
            </button>
          )}
          <button
            type="button"
            onClick={handleCancel}
            style={{
              padding: "10px 16px",
              borderRadius: "8px",
              border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
              background: "transparent",
              color: theme?.text,
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            ยกเลิก
          </button>
          <button
            type="submit"
            style={{
              padding: "10px 24px",
              borderRadius: "8px",
              border: "none",
              background: "#3b82f6",
              color: "#fff",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {editingId ? "💾 บันทึก" : "＋ บันทึกรายการ"}
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto", paddingBottom: "40px" }}>
      {/* 🌟 1. สรุปยอดรวม (Top Cards) 🌟 */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "15px",
          marginBottom: "25px",
        }}
      >
        <div
          style={{ ...cardStyle, textAlign: "center", padding: "20px 10px" }}
        >
          <div
            style={{
              fontSize: "13px",
              color: isDarkMode ? "#94a3b8" : "#64748b",
              fontWeight: "bold",
            }}
          >
            รายรับ / กองกลาง
          </div>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#10b981",
              marginTop: "8px",
            }}
          >
            +฿
            {totalIncome.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </div>
        </div>
        <div
          style={{ ...cardStyle, textAlign: "center", padding: "20px 10px" }}
        >
          <div
            style={{
              fontSize: "13px",
              color: isDarkMode ? "#94a3b8" : "#64748b",
              fontWeight: "bold",
            }}
          >
            รายจ่าย (ส่วนของเรา)
          </div>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: "#ef4444",
              marginTop: "8px",
            }}
          >
            -฿
            {myTotalExpense.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </div>
        </div>
        <div
          style={{
            ...cardStyle,
            textAlign: "center",
            padding: "20px 10px",
            border: `2px solid ${balance >= 0 ? "#10b981" : "#ef4444"}`,
          }}
        >
          <div
            style={{
              fontSize: "13px",
              color: isDarkMode ? "#94a3b8" : "#64748b",
              fontWeight: "bold",
            }}
          >
            ยอดคงเหลือ
          </div>
          <div
            style={{
              fontSize: "24px",
              fontWeight: "bold",
              color: balance >= 0 ? "#10b981" : "#ef4444",
              marginTop: "8px",
            }}
          >
            ฿{balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {/* 🌟 2. แดชบอร์ดรายวัน (Daily Dashboard) 🌟 */}
      <div
        style={{
          ...cardStyle,
          backgroundColor: isDarkMode ? "#1e293b" : "#f8fafc",
          border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
          padding: "20px",
        }}
      >
        <h4
          style={{
            margin: "0 0 15px 0",
            color: theme?.text,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          📅 สรุปรายวัน
        </h4>

        {/* แถบเลือกวันที่ */}
        {uniqueDates.length > 0 && (
          <div
            className="hide-scrollbar"
            style={{
              display: "flex",
              gap: "10px",
              overflowX: "auto",
              paddingBottom: "10px",
              marginBottom: "20px",
              borderBottom: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
            }}
          >
            {uniqueDates.map((date, idx) => {
              const isActive = selectedDate === date;
              const dateObj = new Date(date);
              return (
                <button
                  key={date}
                  onClick={() => {
                    setSelectedDate(date);
                    setSelectedCategoryFilter("all");
                  }}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: "10px 16px",
                    borderRadius: "12px",
                    border: isActive
                      ? "2px solid #3b82f6"
                      : `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
                    background: isActive
                      ? isDarkMode
                        ? "rgba(59,130,246,0.15)"
                        : "#eff6ff"
                      : "transparent",
                    color: isActive
                      ? "#3b82f6"
                      : isDarkMode
                      ? "#94a3b8"
                      : "#64748b",
                    cursor: "pointer",
                    minWidth: "85px",
                    transition: "all 0.2s",
                  }}
                >
                  <span style={{ fontSize: "13px", fontWeight: "bold" }}>
                    วันที่ {idx + 1}
                  </span>
                  <span
                    style={{ fontSize: "11px", opacity: 0.8, marginTop: "2px" }}
                  >
                    {dateObj.toLocaleDateString("th-TH", {
                      day: "numeric",
                      month: "short",
                    })}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {/* ข้อมูลของวันที่เลือก */}
        {selectedDate ? (
          <>
            {/* สรุปรายรับ/รายจ่ายประจำวัน */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "20px",
                background: isDarkMode ? "#0f172a" : "#f8fafc",
                padding: "12px 16px",
                borderRadius: "12px",
                border: `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                    fontWeight: "bold",
                  }}
                >
                  รายจ่ายวันนี้
                </div>
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: "#ef4444",
                  }}
                >
                  ฿
                  {dayExpenseTotal.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </div>
              </div>
              {dayIncomeTotal > 0 && (
                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#94a3b8" : "#64748b",
                      fontWeight: "bold",
                    }}
                  >
                    รายรับวันนี้
                  </div>
                  <div
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: "#10b981",
                    }}
                  >
                    +฿
                    {dayIncomeTotal.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* กราฟโดนัท และตัวกรอง */}
            {dayExpenseTotal > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  marginBottom: "25px",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    width: "130px",
                    height: "130px",
                    flexShrink: 0,
                    margin: "0 auto",
                  }}
                >
                  <svg
                    viewBox="0 0 42 42"
                    width="100%"
                    height="100%"
                    style={{ transform: "rotate(-90deg)" }}
                  >
                    {(() => {
                      let cumulativePercent = 0;
                      return dayBreakdownArray.map((cat) => {
                        const percent = (cat.total / dayExpenseTotal) * 100;
                        const strokeDasharray = `${Math.max(0, percent - 1)} ${
                          100 - Math.max(0, percent - 1)
                        }`;
                        const strokeDashoffset = 100 - cumulativePercent;
                        cumulativePercent += percent;

                        // ไฮไลท์กราฟที่เลือก หมวดอื่นๆ ให้จางลง
                        const isFaded =
                          selectedCategoryFilter !== "all" &&
                          selectedCategoryFilter !== cat.id;

                        return (
                          <circle
                            key={cat.id}
                            cx="21"
                            cy="21"
                            r="15.915"
                            fill="transparent"
                            stroke={cat.color}
                            strokeWidth={
                              selectedCategoryFilter === cat.id ? "8" : "6"
                            }
                            strokeDasharray={strokeDasharray}
                            strokeDashoffset={strokeDashoffset}
                            opacity={isFaded ? 0.2 : 1}
                            style={{ transition: "all 0.3s ease" }}
                          />
                        );
                      });
                    })()}
                  </svg>
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "11px",
                        color: isDarkMode ? "#94a3b8" : "#64748b",
                        fontWeight: "bold",
                        textAlign: "center",
                        maxWidth: "80%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {displayedDonutLabel}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "bold",
                        color: activeCategoryData
                          ? activeCategoryData.color
                          : theme?.text,
                      }}
                    >
                      {displayedDonutTotal >= 10000
                        ? `${(displayedDonutTotal / 1000).toFixed(1)}k`
                        : displayedDonutTotal.toLocaleString(undefined, {
                            minimumFractionDigits:
                              displayedDonutTotal % 1 === 0 ? 0 : 2,
                          })}
                    </div>
                  </div>
                </div>

                {/* Legend รายหมวดหมู่ (ทำหน้าที่เป็น Filter ด้วย) */}
                <div
                  style={{
                    flex: "1 1 200px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      color: theme?.text,
                      marginBottom: "4px",
                    }}
                  >
                    กรองตามหมวดหมู่:
                  </div>
                  <div
                    style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}
                  >
                    <button
                      onClick={() => setSelectedCategoryFilter("all")}
                      style={{
                        padding: "6px 12px",
                        borderRadius: "20px",
                        border:
                          selectedCategoryFilter === "all"
                            ? "1px solid #3b82f6"
                            : `1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}`,
                        background:
                          selectedCategoryFilter === "all"
                            ? "rgba(59, 130, 246, 0.1)"
                            : "transparent",
                        color:
                          selectedCategoryFilter === "all"
                            ? "#3b82f6"
                            : isDarkMode
                            ? "#94a3b8"
                            : "#64748b",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "bold",
                        transition: "all 0.2s",
                      }}
                    >
                      ทั้งหมด
                    </button>
                    {dayBreakdownArray.map((cat) => {
                      const percent = (
                        (cat.total / dayExpenseTotal) *
                        100
                      ).toFixed(0);
                      const isSelected = selectedCategoryFilter === cat.id;
                      const isFiltered = selectedCategoryFilter !== "all";

                      return (
                        <button
                          key={cat.id}
                          onClick={() => setSelectedCategoryFilter(cat.id)}
                          style={{
                            padding: "6px 12px",
                            borderRadius: "20px",
                            border: isSelected
                              ? `1px solid ${cat.color}`
                              : `1px solid ${
                                  isDarkMode ? "#334155" : "#e2e8f0"
                                }`,
                            background: isSelected
                              ? `${cat.color}15`
                              : "transparent",
                            color: isSelected
                              ? cat.color
                              : isDarkMode
                              ? "#94a3b8"
                              : "#64748b",
                            cursor: "pointer",
                            fontSize: "12px",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            opacity: isFiltered && !isSelected ? 0.4 : 1,
                            transition: "all 0.2s",
                          }}
                        >
                          {cat.icon} {cat.label} ({percent}%)
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* 🌟 ปุ่มเพิ่มรายการที่ถูกย้ายมาตรงนี้ 🌟 */}
            <div style={{ marginBottom: "20px", marginTop: "10px" }}>
              {!isAdding && !editingId && (
                <button
                  onClick={() => {
                    setFormData({ ...initialFormState, date: selectedDate });
                    setEditingId(null);
                    setIsAdding(true);
                  }}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "12px",
                    border: `2px dashed ${isDarkMode ? "#475569" : "#cbd5e1"}`,
                    background: "transparent",
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                    fontSize: "14px",
                    fontWeight: "bold",
                    cursor: "pointer",
                    transition: "0.2s",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <span>➕</span> เพิ่มรายการในวันนี้
                </button>
              )}

              {/* ฟอร์มเพิ่มรายการ */}
              {isAdding && !editingId && renderForm()}
            </div>

            {/* รายการธุรกรรมเจาะจงของวันนี้ (ปลดล็อคความสูง) */}
            <div>
              <h5
                style={{
                  margin: "0 0 12px 0",
                  color: theme?.text,
                  fontSize: "14px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>
                  รายการ{" "}
                  {selectedCategoryFilter !== "all"
                    ? `หมวดหมู่: ${
                        allCategories.find(
                          (c) => c.id === selectedCategoryFilter
                        )?.label
                      }`
                    : "ทั้งหมดในวันนี้"}
                </span>
                <span
                  style={{
                    fontSize: "12px",
                    color: isDarkMode ? "#94a3b8" : "#64748b",
                    fontWeight: "normal",
                  }}
                >
                  กดที่รายการเพื่อแก้ไข
                </span>
              </h5>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                }}
              >
                {dayTransactions
                  .filter(
                    (t) =>
                      selectedCategoryFilter === "all" ||
                      t.category === selectedCategoryFilter
                  )
                  .map((t) => {
                    // ถ้ารายการนี้กำลังถูกแก้ไข ให้เรนเดอร์ฟอร์มแก้ไขแทนที่การ์ดเดิม (Inline Edit)
                    if (editingId === t.id) {
                      return (
                        <div key={t.id} style={{ animation: "fadeIn 0.3s" }}>
                          {renderForm()}
                        </div>
                      );
                    }

                    const catMeta = allCategories.find(
                      (c) => c.id === t.category
                    ) || { icon: "📝", color: "#7f8c8d" };
                    const isExpense = t.type === "expense";

                    return (
                      <div
                        key={t.id}
                        onClick={() => handleEditClick(t)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          background: isDarkMode ? "#0f172a" : "#fff",
                          padding: "14px 16px",
                          borderRadius: "12px",
                          border: `1px solid ${
                            isDarkMode ? "#334155" : "#e2e8f0"
                          }`,
                          borderLeft: `4px solid ${
                            isExpense ? "#ef4444" : "#10b981"
                          }`,
                          cursor: "pointer",
                          transition: "all 0.2s ease-in-out",
                          boxShadow: "0 2px 5px rgba(0,0,0,0.02)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateX(4px)";
                          e.currentTarget.style.borderColor = isExpense
                            ? "#ef4444"
                            : "#10b981";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "none";
                          e.currentTarget.style.borderColor = isDarkMode
                            ? "#334155"
                            : "#e2e8f0";
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "15px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "20px",
                              background: `${catMeta.color}15`,
                              padding: "8px",
                              borderRadius: "50%",
                              width: "38px",
                              height: "38px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {catMeta.icon}
                          </div>
                          <div
                            style={{ display: "flex", flexDirection: "column" }}
                          >
                            <span
                              style={{
                                fontSize: "15px",
                                fontWeight: "bold",
                                color: theme?.text,
                              }}
                            >
                              {t.title}
                            </span>
                            {t.note && (
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: isDarkMode ? "#94a3b8" : "#64748b",
                                  marginTop: "2px",
                                }}
                              >
                                {t.note}
                              </span>
                            )}
                          </div>
                        </div>

                        <div style={{ textAlign: "right" }}>
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "bold",
                              color: isExpense ? "#ef4444" : "#10b981",
                            }}
                          >
                            {isExpense ? "-" : "+"}฿
                            {(t.amountPerPerson || t.amount).toLocaleString(
                              undefined,
                              { minimumFractionDigits: 2 }
                            )}
                          </div>

                          {t.currency === "JPY" && (
                            <div
                              style={{
                                fontSize: "11px",
                                color: isDarkMode ? "#64748b" : "#94a3b8",
                                marginTop: "2px",
                              }}
                            >
                              (¥{t.inputCost?.toLocaleString()})
                            </div>
                          )}

                          {isExpense && t.splitCount > 1 && (
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#e67e22",
                                marginTop: "4px",
                                fontWeight: "bold",
                              }}
                            >
                              หาร {t.splitCount} (รวม ฿
                              {t.amount?.toLocaleString()})
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </>
        ) : (
          <div
            style={{
              textAlign: "center",
              color: "#888",
              padding: "40px 20px",
              border: `1px dashed ${isDarkMode ? "#334155" : "#e2e8f0"}`,
              borderRadius: "16px",
            }}
          >
            <div style={{ fontSize: "30px", marginBottom: "10px" }}>💸</div>
            คุณยังไม่มีการบันทึกรายการใดๆ เลย
            <br />
            {/* 🌟 ปุ่มเพิ่มรายการกรณีไม่มีข้อมูล 🌟 */}
            <div style={{ marginTop: "20px" }}>
              {!isAdding && !editingId && (
                <button
                  onClick={() => {
                    setFormData(initialFormState);
                    setEditingId(null);
                    setIsAdding(true);
                  }}
                  style={{
                    padding: "12px 24px",
                    borderRadius: "12px",
                    border: "none",
                    background: "#3b82f6",
                    color: "#fff",
                    fontSize: "14px",
                    fontWeight: "bold",
                    cursor: "pointer",
                  }}
                >
                  ➕ เพิ่มรายการรับ-จ่าย แรกของคุณ
                </button>
              )}
              {isAdding && !editingId && (
                <div style={{ textAlign: "left" }}>{renderForm()}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseTab;
